import React, { useState } from "react";
import {
  X, Check, Pencil, Ban, Send, ShieldCheck, Activity, HardDriveDownload, AlertTriangle,
} from "lucide-react";
import { useIncidentWorkflow } from "../../spine/useIncidentWorkflow";
import { STATUS, STATUS_META, TRANSITIONS } from "../../spine/incidentMachine";

/* Incident command — the §21 workflow, end to end, in one surface.
 *
 * A drawer rather than another rail panel: triaging and authorising is a
 * focused task, and the operator should not be doing it while scanning six
 * other widgets. It is also where the loop stages become visible — each
 * status shows which architectural stage it belongs to, so the demo can
 * point at SENSE → … → LEARN and show it actually happening. */
export default function IncidentCommandDrawer({ lang = "en", incident: demo, officer, onClose }) {
  const wf = useIncidentWorkflow(demo, officer);
  const [reason, setReason] = useState("");
  const [modification, setModification] = useState("");
  const [afterValue, setAfterValue] = useState("");

  if (!demo) return null;
  const L = (o) => o?.[lang] ?? o?.en ?? "";
  const st = wf.incident?.status ?? STATUS.DETECTED;
  const meta = STATUS_META[st];
  const canSenior = officer?.level === "L1" || officer?.level === "L2";

  return (
    <div className="absolute inset-0 z-40 flex justify-end bg-slate-900/40">
      <div className="w-full max-w-md h-full overflow-y-auto bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                {lang === "mr" ? "घटना कमांड" : "Incident Command"}
              </div>
              <div className="text-sm font-black text-slate-900 truncate">{demo.id}</div>
              <div className="text-[11px] text-slate-500 truncate">{L(demo.location)}</div>
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100">
              <X size={16} className="text-slate-500" />
            </button>
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span
              className="rounded px-2 py-0.5 text-[10px] font-black text-white"
              style={{ background: meta.color }}
            >
              {L(meta)}
            </span>
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
              {meta.stage}
            </span>

            {/* Persistence is stated, never implied. */}
            <span
              className="ml-auto inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold text-amber-800"
              title={
                "Stored in this browser only. Editable from devtools, not shared " +
                "with other officers, and lost if site data is cleared. " +
                "Demonstration state, not an audit record."
              }
            >
              <HardDriveDownload size={9} />
              {wf.spine.persistent ? "BROWSER STORE" : "IN MEMORY"}
            </span>
          </div>

          {wf.error && (
            <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-[10px] text-red-800">
              {wf.error}
            </div>
          )}
        </div>

        {/* ── 1. Decision ── */}
        <Section title={lang === "mr" ? "1 · शिफारस" : "1 · Recommendation"}>
          {!wf.decision ? (
            <>
              <p className="text-[11px] text-slate-600 mb-2">
                {lang === "mr"
                  ? "शिफारस नोंदवा — कृती करण्यापूर्वी, जेणेकरून नंतर ती तपासता येईल."
                  : "Record the recommendation as offered, before anyone acts — so it can be audited afterwards."}
              </p>
              <button
                type="button"
                disabled={wf.busy}
                onClick={() => wf.offerDecision({
                  kind: "Crowd diversion",
                  priority: demo.severity === "critical" ? "critical" : "high",
                  title: { en: `Divert inbound flow from ${demo.location?.en}`,
                           mr: `${demo.location?.mr} कडील प्रवाह वळवा` },
                  rationale: { en: `${demo.severity} severity ${demo.type} with active status.`,
                               mr: `सक्रिय ${demo.type} घटना.` },
                  evidence: [
                    `Incident ${demo.id}`,
                    `Severity ${demo.severity}`,
                    `Sector ${demo.sector}`,
                  ],
                  confidence: 0.72,
                  trust: demo.provenance?.trust || "SIMULATED",
                  requiresApproval: true,
                })}
                className="w-full rounded-lg bg-slate-800 px-3 py-2 text-[12px] font-black text-white hover:bg-slate-700 disabled:opacity-50"
              >
                {lang === "mr" ? "शिफारस नोंदवा" : "Record recommendation"}
              </button>
            </>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
              <div className="text-[12px] font-bold text-slate-900">{wf.decision.title_en}</div>
              <div className="text-[11px] text-slate-600 mt-0.5">{wf.decision.rationale_en}</div>
              <div className="mt-1.5 rounded bg-white border border-slate-200 px-1.5 py-1">
                <div className="text-[9px] font-black uppercase tracking-wider text-slate-400">
                  {lang === "mr" ? "पुरावा" : "Evidence"}
                </div>
                {(wf.decision.evidence || []).map((e) => (
                  <div key={e} className="text-[10px] text-slate-600">· {e}</div>
                ))}
              </div>
              <div className="mt-1 text-[9px] text-slate-400">
                engine {wf.decision.engine_version} · confidence{" "}
                {wf.decision.confidence != null ? `${Math.round(wf.decision.confidence * 100)}%` : "—"}
              </div>
            </div>
          )}
        </Section>

        {/* ── 2. Authorise ── */}
        <Section title={lang === "mr" ? "2 · मंजुरी" : "2 · Authorise"}>
          {!wf.decision ? (
            <Muted>{lang === "mr" ? "आधी शिफारस नोंदवा." : "Record a recommendation first."}</Muted>
          ) : !canSenior ? (
            /* Frontend-only, so this check is an affordance and nothing more.
               Anyone can bypass it from devtools. Real authorisation needs a
               server — the rule is stated here so the workflow is honest
               about which of its constraints are enforced and which are not. */
            <Muted>
              {lang === "mr"
                ? "मंजुरीसाठी L1/L2 अधिकारी आवश्यक."
                : "Authorisation requires an L1/L2 officer. Your role can view but not approve."}
            </Muted>
          ) : (
            <>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={lang === "mr" ? "कारण (पर्यायी)" : "Reason (optional)"}
                className="w-full rounded border border-slate-300 px-2 py-1 text-[11px] mb-1.5"
                rows={2}
              />
              <input
                value={modification}
                onChange={(e) => setModification(e.target.value)}
                placeholder={lang === "mr" ? "बदल (Modify साठी आवश्यक)" : "What changed (required to Modify)"}
                className="w-full rounded border border-slate-300 px-2 py-1 text-[11px] mb-2"
              />
              <div className="grid grid-cols-3 gap-1.5">
                <Act icon={Check} tone="emerald" disabled={wf.busy}
                     onClick={() => wf.approve("approve", { reason })}>
                  {lang === "mr" ? "मंजूर" : "Approve"}
                </Act>
                <Act icon={Pencil} tone="amber" disabled={wf.busy || !modification}
                     onClick={() => wf.approve("modify", { modification, reason })}>
                  {lang === "mr" ? "बदल" : "Modify"}
                </Act>
                <Act icon={Ban} tone="red" disabled={wf.busy}
                     onClick={() => wf.approve("reject", { reason })}>
                  {lang === "mr" ? "नाकार" : "Reject"}
                </Act>
              </div>
            </>
          )}

          {wf.approvals.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {wf.approvals.map((a) => (
                <li key={a.id} className="text-[10px] text-slate-600">
                  <span className="font-bold uppercase">{a.action}</span>
                  {a.modification ? ` — ${a.modification}` : ""}
                  {a.reason ? ` · ${a.reason}` : ""}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* ── 3. Dispatch ── */}
        <Section title={lang === "mr" ? "3 · रवाना" : "3 · Dispatch"}>
          {!wf.hasApproval ? (
            <Muted>
              {lang === "mr"
                ? "मंजुरीशिवाय रवाना करता येणार नाही."
                : "Dispatch is blocked until an approval is recorded. This is enforced by the state machine, not the button."}
            </Muted>
          ) : (
            <button
              type="button"
              disabled={wf.busy || wf.tasks.length > 0}
              onClick={() => wf.dispatch([
                { agency: "Police",    instruction: { en: "Meter inbound gate, hold arrivals" } },
                { agency: "Health",    instruction: { en: "Stage ambulance at sector edge" } },
                { agency: "Transport", instruction: { en: "Hold shuttle drop-offs 10 min" } },
              ])}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-600 px-3 py-2 text-[12px] font-black text-white hover:bg-orange-700 disabled:opacity-50"
            >
              <Send size={12} />
              {lang === "mr" ? "3 यंत्रणांना पाठवा" : "Dispatch to 3 agencies"}
            </button>
          )}

          {wf.tasks.map((t) => (
            <div key={t.id} className="mt-1.5 flex items-center gap-2 rounded border border-slate-200 p-1.5">
              <span className="text-[10px] font-black text-slate-700 w-16 flex-shrink-0">{t.agency}</span>
              <span className="text-[10px] text-slate-600 flex-1 truncate">{t.instruction_en}</span>
              {t.status === "acknowledged" ? (
                <span className="text-[9px] font-black text-emerald-700">ACK</span>
              ) : (
                <button
                  type="button"
                  disabled={wf.busy}
                  onClick={() => wf.acknowledge(t.id)}
                  className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold text-slate-700 hover:bg-slate-300"
                >
                  {lang === "mr" ? "स्वीकारा" : "Ack"}
                </button>
              )}
            </div>
          ))}
        </Section>

        {/* ── 4. Verify ── */}
        <Section title={lang === "mr" ? "4 · पडताळणी" : "4 · Verify"}>
          <div className="flex gap-1.5 mb-2">
            <input
              type="number" step="0.1" value={afterValue}
              onChange={(e) => setAfterValue(e.target.value)}
              placeholder={lang === "mr" ? "नंतरची घनता p/m²" : "Density after, p/m²"}
              className="flex-1 rounded border border-slate-300 px-2 py-1 text-[11px]"
            />
            <button
              type="button"
              disabled={wf.busy || !afterValue}
              onClick={() => wf.measureAfter(Number(afterValue))}
              className="rounded bg-slate-800 px-2 py-1 text-[11px] font-bold text-white disabled:opacity-50"
            >
              {lang === "mr" ? "नोंदवा" : "Record"}
            </button>
          </div>

          <div className={`rounded border p-2 text-[10px] ${
            wf.verification.verdict === "improved" ? "border-emerald-300 bg-emerald-50 text-emerald-900"
            : wf.verification.verdict === "worsened" ? "border-red-300 bg-red-50 text-red-900"
            : "border-slate-200 bg-slate-50 text-slate-600"
          }`}>
            <div className="font-black uppercase">{wf.verification.verdict}</div>
            {wf.verification.delta != null ? (
              <div className="mt-0.5 tabular-nums">
                {wf.verification.before} → {wf.verification.after} p/m²
                {wf.verification.pct != null && ` (${wf.verification.pct}%)`}
              </div>
            ) : (
              <div className="mt-0.5">{wf.verification.reason}</div>
            )}
            {wf.verification.caveat && (
              <div className="mt-1 italic opacity-80">{wf.verification.caveat}</div>
            )}
          </div>
        </Section>

        {/* ── 5. Close + measurements ── */}
        <Section title={lang === "mr" ? "5 · निकाली व मापन" : "5 · Resolve & measure"}>
          {TRANSITIONS[st]?.includes(STATUS.RESOLVED) ? (
            <button
              type="button"
              disabled={wf.busy}
              onClick={() => wf.resolve("Conditions returned to normal")}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-[12px] font-black text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <ShieldCheck size={12} />
              {lang === "mr" ? "निकाली काढा" : "Resolve incident"}
            </button>
          ) : (
            <Muted>
              {st === STATUS.RESOLVED
                ? (lang === "mr" ? "निकाली — पुन्हा उघडण्यासाठी नवीन घटना तयार करावी लागेल."
                                 : "Resolved. Reopening requires a new incident referencing this one.")
                : (lang === "mr" ? "आधीचे टप्पे पूर्ण करा." : "Complete the earlier stages first.")}
            </Muted>
          )}

          {wf.metrics && (
            <dl className="mt-2 space-y-0.5">
              <Metric k={lang === "mr" ? "निर्णय वेळ" : "Decision support"} v={wf.metrics.decisionSupportMins} />
              <Metric k={lang === "mr" ? "रवाना" : "Dispatch"} v={wf.metrics.dispatchMins} />
              <Metric k={lang === "mr" ? "स्वीकृती" : "Acknowledgement"} v={wf.metrics.acknowledgementMins} />
              <Metric k={lang === "mr" ? "निराकरण" : "Resolution"} v={wf.metrics.resolutionMins} />
              <div className="text-[9px] text-slate-400 leading-snug pt-0.5">
                {lang === "mr" ? "शोध विलंब" : "Detection latency"}: —{" "}
                {wf.metrics.detectionLatencyNote}
              </div>
            </dl>
          )}
        </Section>

        {/* ── Event log ── */}
        <Section title={lang === "mr" ? "घटनाक्रम" : "Event log"}>
          <div className="mb-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0 text-amber-700" />
            <p className="text-[9px] leading-snug text-amber-900">
              {lang === "mr"
                ? "हा लॉग फक्त या ब्राउझरमध्ये आहे — तो लेखापरीक्षण नोंद नाही."
                : "This log lives in this browser only. It is demonstration state, not an audit record: it can be edited from devtools, no other officer can see it, and clearing site data destroys it."}
              {wf.spine.chain && !wf.spine.chain.intact && (
                <strong className="block mt-0.5">{wf.spine.chain.reason}</strong>
              )}
            </p>
          </div>
          <ol className="space-y-1">
            {wf.events.map((e) => (
              <li key={e.id} className="flex items-start gap-1.5 text-[10px]">
                <Activity size={9} className="mt-0.5 flex-shrink-0 text-slate-400" />
                <span className="text-slate-500 tabular-nums flex-shrink-0">
                  {new Date(e.at).toLocaleTimeString()}
                </span>
                <span className="font-bold text-slate-700">{e.to_status || e.kind}</span>
                {e.actor_agency && <span className="text-slate-400">· {e.actor_agency}</span>}
              </li>
            ))}
          </ol>
        </Section>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="border-b border-slate-200 px-4 py-3">
    <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">{title}</div>
    {children}
  </section>
);

const Muted = ({ children }) => (
  <p className="rounded border border-dashed border-slate-200 px-2 py-2 text-[10px] leading-snug text-slate-500">
    {children}
  </p>
);

const Metric = ({ k, v }) => (
  <div className="flex justify-between text-[10px]">
    <dt className="text-slate-500">{k}</dt>
    <dd className="font-bold text-slate-800 tabular-nums">{v == null ? "—" : `${v} min`}</dd>
  </div>
);

function Act({ icon: Icon, tone, children, ...rest }) {
  const tones = {
    emerald: "bg-emerald-600 hover:bg-emerald-700",
    amber: "bg-amber-600 hover:bg-amber-700",
    red: "bg-red-600 hover:bg-red-700",
  };
  return (
    <button
      type="button"
      {...rest}
      className={`inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-black text-white disabled:opacity-40 ${tones[tone]}`}
    >
      <Icon size={11} />
      {children}
    </button>
  );
}
